import { API_USER_CHAT_IMAGES } from '../../constants/endpoints'
import { controller } from '../../utilities/abortController'
import i18n from '../../utilities/i18n'
import { safeArr } from '../../utilities/safe'

export const IMAGE_CATEGORIES = {
    astrology: { id: 39, label: i18n.t('chatImages.imageCategoryAstrology') },
    compliments: {
        id: 35,
        label: i18n.t('chatImages.imageCategoryCompliments'),
    },
    dancing: { id: 32, label: i18n.t('chatImages.imageCategoryDancing') },
    demotivational: {
        id: 38,
        label: i18n.t('chatImages.imageCategoryDemotivational'),
    },
    emoticons: { id: 29, label: i18n.t('chatImages.imageCategoryEmoticons') },
    flowersAndGifts: {
        id: 28,
        label: i18n.t('chatImages.imageCategoryFlowersAndGifts'),
    },
    general: { id: 27, label: i18n.t('chatImages.imageCategoryGeneral') },
    national: { id: 37, label: i18n.t('chatImages.imageCategoryNational') },
    owned: { id: 30, label: i18n.t('chatImages.imageCategoryOwned') },
    people: { id: 31, label: i18n.t('chatImages.imageCategoryPeople') },
    personal: { id: 40, label: i18n.t('chatImages.imageCategoryPersonal') },
    putDowns: { id: 34, label: i18n.t('chatImages.imageCategoryPutDowns') },
    sexy: { id: 36, label: i18n.t('chatImages.imageCategorySexy') },
    signs: { id: 33, label: i18n.t('chatImages.imageCategorySigns') },
    profilePics: {
        id: 100,
        label: i18n.t('chatImages.imageCategoryProfilePics'),
        user_photos: 1,
    },
    personalLibrary: {
        id: 99,
        label: i18n.t('chatImages.imageCategoryPersonalLibrary'),
        user_library: 1,
    },
    recentlyUsed: {
        label: i18n.t('chatImages.recentlyUsed'),
        chat_image_history: 1,
    },
}

export const SORT_BY = {
    shortcut: {
        param: '&order=shortcut',
        label: i18n.t('chatImages.sortByShortcut'),
    },
    oldest: {
        param: '&order=oldest',
        label: i18n.t('chatImages.sortByOldest'),
    },
    newest: {
        param: '&order=newest',
        label: i18n.t('chatImages.sortByNewest'),
    },
    usage: {
        param: '&order=usage',
        label: i18n.t('chatImages.sortByUsage'),
    },
    frequency: {
        param: '&order=frequency',
        label: i18n.t('chatImages.sortByFrequency'),
    },
} as const

export const SEARCH_TYPE = {
    shortcut: {
        param: '&search_key=shortcut',
        label: i18n.t('chatImages.shortcut'),
    },
    username: {
        param: '&search_key=username',
        label: i18n.t('chatImages.userName'),
    },
} as const

export interface IUserImage {
    user_id: number
    submission_date: string
    daily_use: string
    id: number
    shortcut: string
    src: string
    thumb: string
    username: string
    total_use: number
}

export const searchImage = async (
    query: string,
    searchType?: string,
    sortedBy?: string,
): Promise<Array<IUserImage>> => {
    const { param: nextSearchType } =
        SEARCH_TYPE[searchType] ?? SEARCH_TYPE.shortcut
    const { param: nextSortedBy } = SORT_BY[sortedBy] ?? SORT_BY.usage
    const limit = '&limit=200'
    const url = `${API_USER_CHAT_IMAGES}/client/chatImages?search=${encodeURIComponent(
        query,
    )}${nextSearchType}${nextSortedBy}${limit}`
    try {
        const response = await fetch(url, {
            credentials: 'include',
            signal: controller.signal,
        })
        const { result } = await response.json()
        if (!result || result.success === 0) return []
        const { images } = result
        return safeArr(images as Array<IUserImage>)
    } catch (error) {
        console.error('searchImage error', error)
        return []
    }
}

export const fetchImageCategory = async (
    category: string,
    sortBy?: string,
): Promise<Array<IUserImage>> => {
    const imageCategory = IMAGE_CATEGORIES[category]
    const categoryParam = imageCategory.id
        ? `category=${IMAGE_CATEGORIES[category].id}`
        : ''
    const sortedBy = sortBy || 'usage'
    const { param: sortedByParam } = SORT_BY[sortedBy]
    const userLibrary = imageCategory.user_library
    const userPhotos = imageCategory.user_photos
    const chatImageHistory = imageCategory.chat_image_history
    const userLibraryParam = userLibrary ? `&user_library=${userLibrary}` : ''
    const userPhotosParam = userPhotos ? `&user_photos=${userPhotos}` : ''
    const historyParam = chatImageHistory
        ? `chat_image_history=${chatImageHistory}`
        : ''
    const params = `${historyParam}${categoryParam}${sortedByParam}${userLibraryParam}${userPhotosParam}`
    const limit = '&limit=200'
    const url = `${API_USER_CHAT_IMAGES}/client/chatImages?${params}${limit}`
    try {
        const response = await fetch(url, {
            credentials: 'include',
            signal: controller.signal,
        })
        const { result } = await response.json()
        if (!result || result.success === 0) return []
        const { images } = result
        return safeArr(images as Array<IUserImage>)
    } catch (error) {
        console.error('searchImage error', error)
        return []
    }
}
